import { useEffect, useState } from "react";
import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";
import { SPECIALTIES } from "@/data/heallexa";
import { specialtySlug } from "@/lib/seo-slugs";

// Public read: list_provider_specialties() is SECURITY INVOKER and granted to
// anon/authenticated (no per-user scoping), so this server fn takes no middleware.
export const listProviderSpecialtiesFn = createServerFn({ method: "GET" }).handler(async () => {
  const rows = await db.query(
    "SELECT * FROM public.list_provider_specialties()",
  );
  return rows;
});

let cache: string[] | null = null;
let inflight: Promise<string[]> | null = null;

async function fetchSpecialties(): Promise<string[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await listProviderSpecialtiesFn();
      const dbList = (data ?? [])
        .map((r: any) => (typeof r.specialty === "string" ? r.specialty.trim() : ""))
        .filter(Boolean);
      // Merge curated SPECIALTIES with everything in the DB, dedup case-insensitively.
      const seen = new Set<string>();
      const merged: string[] = [];
      for (const s of [...dbList, ...SPECIALTIES]) {
        const k = s.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        merged.push(s);
      }
      cache = merged;
      return merged;
    } catch {
      cache = [...SPECIALTIES];
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// Resolves a specialty slug against the FULL database-driven list (not just
// the curated SPECIALTIES), for /providers/* routes where the specialty
// picker can produce values outside the curated set. A DB round trip, so
// callers should only reach for this after a fast static-list check fails.
export async function resolveDbSpecialtySlug(slug: string): Promise<string | null> {
  try {
    const rows = await listProviderSpecialtiesFn();
    const target = slug.toLowerCase();
    const match = (rows ?? []).find((r: any) =>
      typeof r.specialty === "string" && specialtySlug(r.specialty.trim()) === target,
    );
    return match ? match.specialty.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Returns every specialty available in the database (merged with the curated
 * default list), cached process-wide so it loads once per session.
 */
export function useAllSpecialties(): string[] {
  const [list, setList] = useState<string[]>(cache ?? SPECIALTIES);
  useEffect(() => {
    let cancelled = false;
    fetchSpecialties().then((l) => { if (!cancelled) setList(l); });
    return () => { cancelled = true; };
  }, []);
  return list;
}
